import React from 'react';
import { Table, Typography } from 'antd';
import { usePermissions } from '../../../hooks/usePermissions';

const { Text } = Typography;

const money = (v) =>
  new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    .format(parseFloat(v || 0));

// Item prices, gross total and processing fee are exact (case bills carry
// unrounded values) — shown to 4 decimals. Only the final amount is rounded.
const money4 = (v) =>
  new Intl.NumberFormat('en-IN', { minimumFractionDigits: 4, maximumFractionDigits: 4 })
    .format(parseFloat(v || 0));

const grams = (v) => (v === null || v === undefined || v === '' ? '—' : Number(v).toFixed(4));

const GoldItemsViewTable = ({ goldDetails = [], processingFees = 0, roundingUpAmount = 0, finalAmount: storedFinalAmount }) => {
  const { canViewField } = usePermissions();

  // Field-level view permission checks
  const canViewItemName = canViewField('billing', 'itemName');
  const canViewPurity = canViewField('billing', 'purity');
  const canViewGrossWeight = canViewField('billing', 'grossWeight');
  const canViewDeduction = canViewField('billing', 'deduction');
  const canViewDustDeduction = canViewField('billing', 'dustDeduction');
  const canViewRatePerGram = canViewField('billing', 'ratePerGram');
  const canViewTotal = canViewField('billing', 'total');

  const totalAmount = goldDetails.reduce((sum, item) => sum + parseFloat(item.totalPrice || 0), 0);
  const fee = parseFloat(processingFees || 0);
  const rounding = parseFloat(roundingUpAmount || 0);
  // The bill's stored final amount is the one the customer was paid; older
  // payloads without it fall back to the same nearest-rupee rounding.
  const finalAmount = storedFinalAmount != null
    ? Number(storedFinalAmount)
    : Math.round(Math.round((totalAmount - fee + rounding) * 10000) / 10000);
  // The automatic nearest-rupee round-off inside the final amount.
  const roundOff = Math.round((finalAmount - (totalAmount - fee + rounding)) * 10000) / 10000;

  const columns = [
    {
      title: '#',
      key: 'index',
      width: 48,
      align: 'center',
      render: (_, __, index) => <Text type="secondary">{index + 1}</Text>,
    },
    canViewItemName && {
      title: 'Item Name',
      dataIndex: 'itemName',
      key: 'itemName',
      render: (text) => <Text strong>{text || '—'}</Text>,
    },
    canViewPurity && {
      title: 'Purity',
      dataIndex: 'purity',
      key: 'purity',
      align: 'center',
      render: (text) => <Text>{text || '—'}</Text>,
    },
    canViewGrossWeight && {
      title: 'Gross Wt. (g)',
      dataIndex: 'grossWeight',
      key: 'grossWeight',
      align: 'right',
      render: grams,
    },
    canViewDeduction && {
      title: 'Stone/Wax Ded. (g)',
      dataIndex: 'deduction',
      key: 'deduction',
      align: 'right',
      render: grams,
    },
    canViewDustDeduction && {
      title: 'Dust Ded. (g)',
      dataIndex: 'dustDeduction',
      key: 'dustDeduction',
      align: 'right',
      render: grams,
    },
    {
      title: 'Net Wt. (g)',
      dataIndex: 'netWeight',
      key: 'netWeight',
      align: 'right',
      render: (v) => <Text strong>{grams(v)}</Text>,
    },
    canViewRatePerGram && {
      title: 'Rate / g',
      dataIndex: 'ratePerGram',
      key: 'ratePerGram',
      align: 'right',
      render: (value) => <Text>₹ {money(value)}</Text>,
    },
    canViewTotal && {
      title: 'Total Price',
      dataIndex: 'totalPrice',
      key: 'totalPrice',
      align: 'right',
      render: (value) => <Text strong>₹ {money4(value)}</Text>,
    },
  ].filter(Boolean);

  // Summary rows must span every column except the last, and the column count
  // moves with the user's field permissions — a hardcoded span misaligns the
  // totals the moment any column is hidden.
  const labelSpan = Math.max(1, columns.length - 1);

  const summaryRow = (label, value, opts = {}) => (
    <Table.Summary.Row className={opts.emphasis ? 'bill-items__final' : undefined}>
      <Table.Summary.Cell index={0} colSpan={labelSpan} align="right">
        <Text strong={!!opts.emphasis} type={opts.emphasis ? undefined : 'secondary'}>
          {label}
        </Text>
      </Table.Summary.Cell>
      <Table.Summary.Cell index={1} align="right">
        <Text strong style={{ color: opts.color, fontSize: opts.emphasis ? 15 : undefined }}>
          {value}
        </Text>
      </Table.Summary.Cell>
    </Table.Summary.Row>
  );

  const dataSource = goldDetails.map((item, index) => ({ ...item, key: index }));

  return (
    <div className="bill-items">
      <Table
        size="small"
        columns={columns}
        dataSource={dataSource}
        pagination={false}
        bordered
        scroll={{ x: 'max-content' }}
        summary={() => (
          <>
            {summaryRow('Gross Total', `₹ ${money4(totalAmount)}`)}
            {fee > 0 && summaryRow('Processing Fees', `- ₹ ${money4(fee)}`, { color: '#cf1322' })}
            {rounding !== 0 && summaryRow(
              'Rounding Adjustment',
              `${rounding > 0 ? '+' : '-'} ₹ ${money(Math.abs(rounding))}`,
              { color: rounding > 0 ? '#389e0d' : '#cf1322' },
            )}
            {roundOff !== 0 && summaryRow(
              'Round Off (to nearest ₹)',
              `${roundOff > 0 ? '+' : '-'} ₹ ${money4(Math.abs(roundOff))}`,
            )}
            {summaryRow('Final Amount', `₹ ${money(finalAmount)}`, { emphasis: true })}
          </>
        )}
      />
    </div>
  );
};

export default GoldItemsViewTable;
